import { EnvironmentOutlined, ExportOutlined } from '@ant-design/icons';
import { Card, Button, Modal, DatePicker, message } from 'antd';
import GoogleMapReact from 'google-map-react';
import { UserLocation } from '../types/user.types';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { useState } from 'react';

const { RangePicker } = DatePicker;

interface UserLocationsMapProps {
	userLocations: UserLocation[];
}

// Marker component for the map
interface MarkerProps {
	lat: number;
	lng: number;
	text?: string;
}

const Marker = ({ text }: MarkerProps) => (
	<div className="flex flex-col items-center">
		<EnvironmentOutlined className="text-2xl text-red-500" />
		{text && <div className="mt-1 text-xs font-bold">{text}</div>}
	</div>
);

export const UserLocationsMap = ({ userLocations }: UserLocationsMapProps) => {
	const [isExportModalVisible, setIsExportModalVisible] = useState(false);
	const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);

	if (userLocations.length === 0) {
		return null;
	}

	const handleExport = () => {
		if (!dateRange || !dateRange[0] || !dateRange[1]) {
			message.error('Please select a date range');
			return;
		}

		const startDate = dateRange[0].startOf('day');
		const endDate = dateRange[1].endOf('day');

		// Filter locations by date range
		const filteredLocations = userLocations.filter((location) => {
			const locationDate = dayjs(location.created_at);
			return (
				(locationDate.isAfter(startDate) || locationDate.isSame(startDate)) &&
				(locationDate.isBefore(endDate) || locationDate.isSame(endDate))
			);
		});

		if (filteredLocations.length === 0) {
			message.warning('No locations found in the selected date range');
			return;
		}

		// Prepare data for Excel export
		const exportData = filteredLocations.map((location, index) => ({
			'Location #': index + 1,
			Latitude: location.latitude,
			Longitude: location.longitude,
			'Number of Times': location.number_of_time,
			'Created At': dayjs(location.created_at).format('DD-MM-YYYY LT'),
			'Updated At': dayjs(location.updated_at).format('DD-MM-YYYY LT'),
		}));

		// Create workbook and worksheet
		const wb = XLSX.utils.book_new();
		const ws = XLSX.utils.json_to_sheet(exportData);

		// Add worksheet to workbook
		XLSX.utils.book_append_sheet(wb, ws, 'User Locations');

		// Generate filename with date range
		const filename = `user_locations_${startDate.format('DD-MM-YYYY')}_to_${endDate.format('DD-MM-YYYY')}.xlsx`;

		// Save the file
		XLSX.writeFile(wb, filename);

		message.success(`Exported ${filteredLocations.length} locations to ${filename}`);
		setIsExportModalVisible(false);
		setDateRange(null);
	};

	const showExportModal = () => {
		setIsExportModalVisible(true);
	};

	const handleCancel = () => {
		setIsExportModalVisible(false);
		setDateRange(null);
	};

	return (
		<>
			<Card
				title="User Locations Map"
				className="shadow-sm"
				extra={
					<Button
						type="primary"
						icon={<ExportOutlined />}
						onClick={showExportModal}
						size="small"
					>
						Export to Excel
					</Button>
				}
			>
				<div className="h-[400px] w-full">
					<GoogleMapReact
						bootstrapURLKeys={{
							key: 'AIzaSyBRsT8V9sgPaWfiW6P7SsVW1L5pXoCHXP8',
						}}
						defaultCenter={{
							lat: userLocations[0] ? parseFloat(userLocations[0].latitude) : 0,
							lng: userLocations[0] ? parseFloat(userLocations[0].longitude) : 0,
						}}
						defaultZoom={16}
					>
						{userLocations.map((location, index) => (
							<Marker
								key={index}
								lat={parseFloat(location.latitude)}
								lng={parseFloat(location.longitude)}
								text={`Location ${index + 1} - ${dayjs(location.created_at).format('DD/MM/YYYY LT')}`}
							/>
						))}
					</GoogleMapReact>
				</div>
			</Card>

			{/* Export Modal */}
			<Modal
				title="Export User Locations"
				open={isExportModalVisible}
				onCancel={handleCancel}
				footer={[
					<Button
						key="cancel"
						onClick={handleCancel}
					>
						Cancel
					</Button>,
					<Button
						key="export"
						type="primary"
						onClick={handleExport}
					>
						Export
					</Button>,
				]}
			>
				<div className="space-y-4">
					<div>
						<p className="mb-2 text-sm text-gray-600">Select a date range to filter the locations before exporting:</p>
						<RangePicker
							value={dateRange}
							onChange={setDateRange}
							format="DD-MM-YYYY"
							placeholder={['Start Date', 'End Date']}
							style={{ width: '100%' }}
						/>
					</div>
					<div className="text-xs text-gray-500">Total locations available: {userLocations.length}</div>
				</div>
			</Modal>
		</>
	);
};
